import React, { useState, useEffect } from "react";
import WindowHeader from "../Layout/WindowHeader/WindowHeader";
import { FiSave, FiLogOut } from "react-icons/fi";
import { useDbContext } from "../../context/DbContext";
import axios from "axios";

const customGridLogo =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='3' width='7' height='9' rx='1'/><rect x='14' y='3' width='7' height='5' rx='1'/><rect x='14' y='12' width='7' height='9' rx='1'/><rect x='3' y='16' width='7' height='5' rx='1'/></svg>";

const GridDesign = ({
  onClose,
  onSave,
  formName,
  initialColumns = [],
  zIndex,
}) => {
  const modalZIndex = (zIndex ?? 9999) + 10;
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Drag and Drop States
  const [draggedIdx, setDraggedIdx] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === index) return;

    // Live swap items in state
    const reorderedCols = [...columns];
    const [draggedItem] = reorderedCols.splice(draggedIdx, 1);
    reorderedCols.splice(index, 0, draggedItem);

    setColumns(reorderedCols);
    setDraggedIdx(index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    // Re-index all orderNo and sNo values sequentially
    const finalCols = columns.map((col, idx) => ({
      ...col,
      orderNo: idx,
      sNo: idx + 1,
    }));
    setColumns(finalCols);
    setDraggedIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
  };

  useEffect(() => {
    const fetchGridDesign = async () => {
      if (!dbCode || !formName) {
        setColumns(initialColumns);
        setLoading(false);
        return;
      }

      // Check Local Storage first
      let cached = localStorage.getItem(`grid_design_${formName}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const itemNameCol = parsed.find((c) => c.columnName === "ItemName");
          const targetWidth = formName.toLowerCase().includes("purchase")
            ? 75
            : 130;
          if (itemNameCol && itemNameCol.width !== targetWidth) {
            localStorage.removeItem(`grid_design_${formName}`);
            cached = null;
          } else {
            setColumns(parsed);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Error parsing cached grid design:", e);
        }
      }

      try {
        const response = await axios.get(
          `${apiBaseUrl}/main/griddesign/${dbCode}/${formName}`,
        );
        const dbCols = response.data.recordset || response.data || [];

        if (dbCols.length > 0) {
          // Merge db columns with initialColumns
          const merged = initialColumns.map((initCol, idx) => {
            const dbMatch = dbCols.find(
              (c) =>
                String(c.ColName).toLowerCase() ===
                String(initCol.columnName).toLowerCase(),
            );

            if (dbMatch) {
              return {
                ...initCol,
                width: dbMatch.Width !== null ? dbMatch.Width : initCol.width,
                orderNo: dbMatch.InNo !== null ? dbMatch.InNo : initCol.orderNo,
                header: dbMatch.Header || initCol.header,
                show: dbMatch.Vis === 1,
                textBox: dbMatch.TextBox || initCol.textBox,
                label: dbMatch.Label || initCol.label,
                tabStop:
                  dbMatch.TabStop !== null ? dbMatch.TabStop : initCol.tabStop,
              };
            }
            return initCol;
          });

          // Sort by orderNo
          merged.sort((a, b) => a.orderNo - b.orderNo);

          // Re-index sNo
          const reindexed = merged.map((col, idx) => ({
            ...col,
            sNo: idx + 1,
          }));

          // Automatically cache in local storage on successful fetch
          localStorage.setItem(
            `grid_design_${formName}`,
            JSON.stringify(reindexed),
          );

          setColumns(reindexed);
        } else {
          setColumns(initialColumns);
        }
      } catch (error) {
        console.error("Error fetching grid design:", error);
        setColumns(initialColumns);
      } finally {
        setLoading(false);
      }
    };

    fetchGridDesign();
  }, [dbCode, formName, apiBaseUrl]);

  const handleWidthChange = (index, val) => {
    const updated = [...columns];
    updated[index].width = val === "" ? "" : Number(val);
    setColumns(updated);
  };

  const handleOrderNoChange = (index, val) => {
    const updated = [...columns];
    updated[index].orderNo = val === "" ? "" : Number(val);
    setColumns(updated);
  };

  const handleHeaderChange = (index, val) => {
    const updated = [...columns];
    updated[index].header = val;
    setColumns(updated);
  };

  const handleShowChange = (index) => {
    const updated = [...columns];
    updated[index].show = !updated[index].show;
    setColumns(updated);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    try {
      if (dbCode && formName) {
        await axios.post(
          `${apiBaseUrl}/main/griddesign/${dbCode}/${formName}`,
          columns,
        );
        // Automatically save to local storage
        localStorage.setItem(
          `grid_design_${formName}`,
          JSON.stringify(columns),
        );
      }

      if (onSave) {
        onSave(columns);
      }
      alert("Grid settings saved successfully!");
    } catch (error) {
      console.error("Error saving grid design:", error);
      alert("Failed to save grid configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetClick = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reset grid settings to default?",
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      if (dbCode && formName) {
        // Send the initial default columns to save default settings in database
        await axios.post(
          `${apiBaseUrl}/main/griddesign/${dbCode}/${formName}`,
          initialColumns,
        );
        localStorage.setItem(
          `grid_design_${formName}`,
          JSON.stringify(initialColumns),
        );
      } else {
        localStorage.setItem(
          `grid_design_${formName || "default"}`,
          JSON.stringify(initialColumns),
        );
      }

      setColumns(initialColumns);

      if (onSave) {
        onSave(initialColumns);
      }

      alert("Grid design reset to default successfully!");
    } catch (error) {
      console.error("Error resetting grid design:", error);
      alert("Failed to reset grid configuration.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-auto font-Inter"
        style={{ zIndex: modalZIndex }}
      >
        <div className="bg-white p-6 rounded-md shadow-lg text-[13px] text-gray-700 font-medium">
          Loading Grid Design...
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-auto font-Inter"
      style={{ zIndex: modalZIndex }}
    >
      <div
        className="w-[1000px] bg-white border border-[#b8cce0] shadow-2xl rounded-[6px] overflow-hidden flex flex-col"
        style={{ minWidth: "800px", minHeight: "500px" }}
      >
        <WindowHeader
          title={`Grid Design - ${formName || ""}`}
          logo={customGridLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        <div className="p-4 flex flex-col gap-4 flex-1">
          {/* Table Container */}
          <div className="border border-[#b8cce0] rounded-[4px] overflow-hidden">
            <div className="max-h-[380px] overflow-y-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#1a3abf] text-white">
                    <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[60px]">
                      SINo
                    </th>
                    <th className="py-1.5 px-4 font-semibold text-left border-r border-white/20">
                      ColumnName
                    </th>
                    <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[90px]">
                      Width
                    </th>
                    <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[90px]">
                      OrderNo
                    </th>
                    <th className="py-1.5 px-4 font-semibold text-left border-r border-white/20 w-[180px]">
                      Header
                    </th>
                    <th className="py-1.5 px-3 font-semibold text-center w-[70px]">
                      Show
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#d6e4f3]">
                  {columns.map((col, idx) => {
                    const isDragged = draggedIdx === idx;
                    return (
                      <tr
                        key={col.columnName || idx}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={handleDrop}
                        onDragEnd={handleDragEnd}
                        className={`${
                          idx % 2 === 0 ? "bg-[#edf4fc]" : "bg-white"
                        } hover:bg-blue-50/50 transition-all duration-300 cursor-move ${
                          isDragged ? "opacity-85 bg-[#dbeafe]" : ""
                        }`}
                        style={
                          isDragged
                            ? {
                                outline: "2.5px dashed #2563eb",
                                outlineOffset: "-2.5px",
                                position: "relative",
                                zIndex: 2,
                              }
                            : undefined
                        }
                      >
                        {/* SINo */}
                        <td className="py-1 px-3 text-center text-gray-700 font-medium border-r border-[#d6e4f3] select-none">
                          {col.sNo}
                        </td>

                        {/* ColumnName */}
                        <td className="py-1 px-4 text-left text-gray-700 font-medium border-r border-[#d6e4f3] select-none">
                          {col.columnName}
                        </td>

                        {/* Width */}
                        <td className="py-1 px-3 text-center border-r border-[#d6e4f3]">
                          <input
                            type="number"
                            className="w-full h-7 text-center text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                            value={col.width}
                            onChange={(e) =>
                              handleWidthChange(idx, e.target.value)
                            }
                          />
                        </td>

                        {/* OrderNo */}
                        <td className="py-1 px-3 text-center border-r border-[#d6e4f3]">
                          <input
                            type="number"
                            className="w-full h-7 text-center text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                            value={col.orderNo}
                            onChange={(e) =>
                              handleOrderNoChange(idx, e.target.value)
                            }
                          />
                        </td>

                        {/* Header */}
                        <td className="py-1 px-3 text-left border-r border-[#d6e4f3]">
                          <input
                            type="text"
                            className="w-full h-7 px-2.5 text-left text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                            value={col.header}
                            onChange={(e) =>
                              handleHeaderChange(idx, e.target.value)
                            }
                          />
                        </td>

                        {/* Show */}
                        <td className="py-1 px-3 text-center">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded text-[#1a3abf] border-[#cbd5e1] focus:ring-[#1a3abf] cursor-pointer"
                              checked={col.show}
                              onChange={() => handleShowChange(idx)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 mt-auto pt-2 border-t border-[#e2e8f0]">
            <button
              onClick={handleResetClick}
              disabled={saving}
              className="px-5 text-[13px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 disabled:bg-red-100 rounded-[4px] transition-colors border border-red-200 cursor-pointer flex items-center gap-2"
              style={{ height: "34px" }}
            >
              Reset to Default
            </button>
            <button
              onClick={handleSaveClick}
              disabled={saving}
              className="px-5 text-[13px] font-semibold text-white bg-[#1a3abf] hover:bg-blue-800 disabled:bg-blue-300 rounded-[4px] transition-colors border-none cursor-pointer flex items-center gap-2"
              style={{ height: "34px" }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="text-base" />
                  Save
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-5 text-[13px] font-semibold text-[#334155] bg-[#EAF0FC] hover:bg-blue-100 rounded-[4px] transition-colors border border-[#b8cce0]/50 cursor-pointer flex items-center gap-2"
              style={{ height: "34px" }}
            >
              <FiLogOut className="text-base" />
              Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridDesign;
